import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * Bespoke behaviour for the Mikolito product page (sections/mikolito-product.liquid):
 * gallery thumbnail swap, FAQ accordion, sticky buy bar, and morphing the custom
 * (non-block) variant-dependent regions – delivery badges, savings
 * badge, guarantees – whenever <variant-picker> reports a variant change.
 *
 * Price/variant-picker/buy-buttons themselves already self-update via their own
 * theme components (product-price.js, variant-picker.js, product-form.js); this
 * only covers the plain markup this section adds around them.
 */
class MikolitoProductPage extends HTMLElement {
  connectedCallback() {
    this.#setupGallery();
    this.#setupFaq();
    this.#setupShare();
    this.#setupSectionNav();
    this.#setupStickyBar();
    this.#setupVariantReactiveRegions();
  }

  #setupGallery() {
    const main = this.querySelector('[data-gallery-main]');
    const thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const { full, alt } = thumb.dataset;
        if (full) {
          // srcset ma pierwszeństwo przed src – bez usunięcia go zdjęcie się nie zmienia.
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.setAttribute('src', full);
        }
        if (alt) main.setAttribute('alt', alt);
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      });
    });
  }

  #setupFaq() {
    this.querySelectorAll('[data-faq-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const answer = button.nextElementSibling;
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        if (answer) answer.classList.toggle('is-open', !isOpen);
      });
    });
  }

  #setupSectionNav() {
    const nav = this.querySelector('[data-section-nav]');
    if (!nav) return;

    // Sekcje mogą być w innym pliku sekcji (opinie Trustoo, FAQ, Szczegóły) – szukamy po id w całym dokumencie.
    const items = Array.from(nav.querySelectorAll('[data-nav-link]'))
      .map((link) => ({ link, target: document.getElementById((link.getAttribute('href') || '').slice(1)) }))
      .filter((item) => {
        // Trustoo doładowuje widget asynchronicznie – link zostaje, dopóki cel może jeszcze się pojawić.
        const isTrustoo = item.link.getAttribute('href') === '#trustoo-widget-wrapper';
        if (!item.target && !isTrustoo) item.link.remove();
        return item.target || isTrustoo;
      });

    const offset = () => {
      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      return header + nav.offsetHeight + 12;
    };
    const targetOf = (item) => item.target || document.getElementById('trustoo-widget-wrapper');

    items.forEach((item) => {
      item.link.addEventListener('click', (event) => {
        const target = targetOf(item);
        if (!target) return;
        event.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - offset() + 1;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });

    // Slot trzyma miejsce w układzie, gdy pasek jest przyklejony (fixed), żeby treść nie skakała.
    const slot = nav.closest('[data-nav-slot]');
    const updateStuck = () => {
      if (!slot) return;
      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const wasStuck = nav.classList.contains('is-stuck');
      if (!wasStuck) slot.style.height = nav.offsetHeight + 'px';
      const stuck = slot.getBoundingClientRect().top <= header;
      nav.classList.toggle('is-stuck', stuck);
    };

    let ticking = false;
    const updateActive = () => {
      ticking = false;
      updateStuck();
      const line = offset() + 8;
      let active = null;
      items.forEach((item) => {
        const target = targetOf(item);
        if (target && target.getBoundingClientRect().top <= line) active = item;
      });
      items.forEach((item) => {
        const isActive = item === active;
        item.link.classList.toggle('is-active', isActive);
        if (isActive && nav.classList.contains('is-stuck')) {
          const strip = item.link.parentElement;
          if (strip) strip.scrollLeft = item.link.offsetLeft - strip.clientWidth / 2 + item.link.clientWidth / 2;
        }
      });
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updateActive);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', updateActive, { passive: true });
    updateActive();
  }

  #setupShare() {
    this.querySelectorAll('[data-mik-share]').forEach((button) => {
      button.addEventListener('click', async () => {
        const { url, title } = button.dataset;
        if (!url) return;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch {
            /* użytkownik anulował */
          }
          return;
        }
        // Przeglądarki bez natywnego menu udostępniania (głównie desktop) – kopiuj link.
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          return;
        }
        const originalHtml = button.innerHTML;
        button.textContent = '✓';
        button.setAttribute('aria-label', 'Link skopiowany');
        setTimeout(() => {
          button.innerHTML = originalHtml;
          button.setAttribute('aria-label', 'Udostępnij produkt');
        }, 1800);
      });
    });
  }

  #setupStickyBar() {
    const hero = this.querySelector('[data-hero]');
    const bar = this.querySelector('[data-sticky-bar]');
    if (!hero || !bar || typeof IntersectionObserver === 'undefined') return;

    bar.removeAttribute('hidden');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.toggleAttribute('data-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(hero);

    const scrollButton = bar.querySelector('[data-sticky-scroll-to-buybox]');
    const buybox = this.querySelector('#mikolito-product-buybox');
    scrollButton?.addEventListener('click', () => {
      buybox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  #setupVariantReactiveRegions() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener(StandardEvents.productSelect, (event) => {
      if (!(event.target instanceof Element) || !event.promise) return;

      event.promise
        .then(({ detail }) => {
          if (!detail?.html) return;
          [
            '[data-variant-badges]',
            '[data-save-badge-slot]',
            '[data-guarantees]',
            '[data-preorder-region]',
          ].forEach(
            (selector) => this.#morphRegion(detail.html, selector)
          );
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[mikolito-product] variant update failed:', error);
        });
    });
  }

  /**
   * @param {Document} newHtml
   * @param {string} selector
   */
  #morphRegion(newHtml, selector) {
    const current = this.querySelector(selector);
    const next = newHtml.querySelector(selector);
    if (!current || !next) return;
    // Zachowaj wpisaną dedykację przy przełączaniu wariantów (np. przy zmianie wariantu).
    const dedication = current.querySelector('[data-dedication]');
    const typed = dedication ? dedication.value : '';
    morph(current, next);
    const restored = current.querySelector('[data-dedication]');
    if (restored && typed) restored.value = typed;
  }
}

if (!customElements.get('mikolito-product-page')) {
  customElements.define('mikolito-product-page', MikolitoProductPage);
}
